import RequestManager from '../RequestManager';
import JsFeeder from '../../AAFPersistenceManager/feeders/JsFeeder';
import EpgVersionStatus from '../../utils/EpgVersionStatus';
import NewRelic from '../../utils/trackers/NewRelic';

const API_CALL_EVENT_TYPE = 'BrowserApiCall';
const HTTP_SUCCESS_MIN = 200;
const HTTP_SUCCESS_MAX = 400;
const NETWORK_ERROR_STATUS = 0;
const SKIPPED_ENDPOINT_PATHS = ['/webapi-video/check-online'];

let requestManagerWrapped = false;
let selectiveFetchWrapped = false;

const toMethod = (method = 'GET') => String(method || 'GET').toUpperCase();

const toStatus = (candidate, fallbackStatus = NETWORK_ERROR_STATUS) => {
  const numericStatus = Number(candidate);

  return Number.isFinite(numericStatus) ? numericStatus : fallbackStatus;
};

const isSuccessfulStatus = (status) => status >= HTTP_SUCCESS_MIN && status < HTTP_SUCCESS_MAX;

const toComparablePath = (endpoint = '') => {
  const normalizedEndpoint = String(endpoint || '');

  try {
    return new URL(normalizedEndpoint, 'https://clarovideo.local').pathname;
  } catch (error) {
    return normalizedEndpoint.split('?')[0];
  }
};

const shouldEmitApiCall = (endpoint) => {
  const comparablePath = toComparablePath(endpoint);

  return !SKIPPED_ENDPOINT_PATHS.some((skippedPath) => comparablePath === skippedPath);
};

const buildApiCallPayload = ({ endpoint, method, status, startedAt, result }) => {
  const timestamp = Date.now();

  return {
    eventType: API_CALL_EVENT_TYPE,
    endpoint,
    method,
    status,
    duration: timestamp - startedAt,
    timestamp,
    result,
  };
};

const emitApiCall = (payload) => {
  try {
    if (!shouldEmitApiCall(payload && payload.endpoint)) {
      return;
    }

    if (NewRelic && typeof NewRelic.recordCustomEvent === 'function') {
      NewRelic.recordCustomEvent(API_CALL_EVENT_TYPE, payload);
    }
  } catch (error) {
    // Telemetry must never affect request behavior.
  }
};

const resolveFetchEndpoint = (input) => {
  if (typeof input === 'string') {
    return input;
  }

  if (input && input.url) {
    return input.url;
  }

  return '';
};

const resolveFetchMethod = (input, init = {}) => {
  if (init && init.method) {
    return toMethod(init.method);
  }

  if (input && input.method) {
    return toMethod(input.method);
  }

  return 'GET';
};

const instrumentFetch = (originalFetch) => async (input, init) => {
  const startedAt = Date.now();
  const endpoint = resolveFetchEndpoint(input);
  const method = resolveFetchMethod(input, init);

  try {
    const response = await originalFetch(input, init);
    const status = toStatus(response && response.status, NETWORK_ERROR_STATUS);

    emitApiCall(buildApiCallPayload({
      endpoint,
      method,
      status,
      result: isSuccessfulStatus(status) ? 'success' : 'failure',
      startedAt,
    }));

    return response;
  } catch (error) {
    emitApiCall(buildApiCallPayload({
      endpoint,
      method,
      status: NETWORK_ERROR_STATUS,
      result: 'failure',
      startedAt,
    }));

    throw error;
  }
};

const withTrackedFetch = (callback) => {
  const runtime = typeof globalThis !== 'undefined' ? globalThis : window;
  const originalFetch = runtime && runtime.fetch;

  if (!runtime || typeof originalFetch !== 'function') {
    return callback();
  }

  runtime.fetch = instrumentFetch(originalFetch);

  try {
    return Promise.resolve(callback()).then(
      (result) => {
        runtime.fetch = originalFetch;
        return result;
      },
      (error) => {
        runtime.fetch = originalFetch;
        throw error;
      }
    );
  } catch (error) {
    runtime.fetch = originalFetch;
    throw error;
  }
};

export const wrapRequestManager = () => {
  if (requestManagerWrapped) {
    return;
  }

  const originalAddRequest = RequestManager.addRequest;

  RequestManager.addRequest = async (request) => {
    const startedAt = Date.now();
    const endpoint = request && typeof request.getUrl === 'function' ? request.getUrl() : '';
    const method = request && typeof request.getMethod === 'function' ? toMethod(request.getMethod()) : 'GET';

    try {
      const response = await originalAddRequest.call(RequestManager, request);
      const status = toStatus(response && (response.status || (response.response && response.response.status)), 200);

      emitApiCall(buildApiCallPayload({
        endpoint,
        method,
        status,
        result: isSuccessfulStatus(status) ? 'success' : 'failure',
        startedAt,
      }));

      return response;
    } catch (error) {
      const status = toStatus(error && (error.status || (error.response && error.response.status)), NETWORK_ERROR_STATUS);

      emitApiCall(buildApiCallPayload({
        endpoint,
        method,
        status,
        result: 'failure',
        startedAt,
      }));

      throw error;
    }
  };

  requestManagerWrapped = true;
};

export const wrapSelectiveFetch = () => {
  if (selectiveFetchWrapped) {
    return;
  }

  const originalUpdateOfflineEpg = JsFeeder.prototype.updateOfflineEpg;
  const originalResetApp = EpgVersionStatus.prototype.resetApp;

  JsFeeder.prototype.updateOfflineEpg = function(...args) {
    return withTrackedFetch(() => originalUpdateOfflineEpg.apply(this, args));
  };

  EpgVersionStatus.prototype.resetApp = function(...args) {
    // Scoped fetch instrumentation keeps telemetry limited to this known direct-fetch flow.
    return withTrackedFetch(() => originalResetApp.apply(this, args));
  };

  selectiveFetchWrapped = true;
};

export const initNewRelicInterceptor = () => {
  wrapRequestManager();
  wrapSelectiveFetch();
};

export default initNewRelicInterceptor;



// WEBPACK FOOTER //
// ./src/requests/interceptors/newRelicInterceptor.js